import React, { useState, useEffect } from "react";
import { adminLogin, adminStats, adminExportUrl, adminContributions, adminContributionsExportUrl, adminLessons, moderateLesson, moderateComment } from "../lib/api";
import { RC_ICON } from "../lib/constants";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { Lock, Download, Users, CheckCircle2, Clock, LogOut, MessageSquare, ClipboardList, Eye, Lightbulb, Check, Trash2, Zap } from "lucide-react";

const TOKEN_KEY = "svdp_admin_token";
const BLUES = ["#6B4423", "#B07A3C", "#5A2A85", "#365C4C", "#2C2117", "#C99A5B"];

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white rounded-lg border border-[var(--line)] p-5">
    <div className="flex items-center gap-2 text-vin mb-2">
      <Icon className="w-4 h-4" strokeWidth={1.75} />
      <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-vin-dark/70">{label}</span>
    </div>
    <div className="font-display text-3xl font-semibold text-ink">{value}</div>
    {sub && <div className="text-xs text-ink-soft mt-1">{sub}</div>}
  </div>
);

const BarBlock = ({ title, data }) => (
  <div className="bg-white rounded-lg border border-[var(--line)] p-6">
    <h3 className="font-display text-lg font-semibold text-ink mb-4">{title}</h3>
    {data.length === 0 ? (
      <p className="text-sm text-ink-soft">No data yet.</p>
    ) : (
      <ResponsiveContainer width="100%" height={Math.max(160, data.length * 40)}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="label"
            width={180}
            tick={{ fontSize: 12, fill: "#4A5B6B" }}
            axisLine={false}
            tickLine={false}
          />
          <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={18}>
            {data.map((_, i) => (
              <Cell key={i} fill={BLUES[i % BLUES.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { token } = await adminLogin(email, password);
      localStorage.setItem(TOKEN_KEY, token);
      onSuccess(token);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-[var(--page)]">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-lg border border-[var(--line)] p-8">
        <img src={RC_ICON} alt="Root Causes Challenge" className="h-14 w-14 mx-auto mb-5 rounded-lg" />
        <h1 className="font-display text-2xl font-semibold text-ink text-center">Administrator</h1>
        <p className="text-sm text-ink-soft text-center mt-1">Root Causes Challenge</p>
        <div className="mt-6 space-y-3">
          <input
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full rounded-lg border border-[var(--line)] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-vin/40 focus:border-vin"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full rounded-lg border border-[var(--line)] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-vin/40 focus:border-vin"
          />
        </div>
        {error && <p data-testid="admin-error" className="text-sm text-[#8E2929] mt-3">{error}</p>}
        <button
          data-testid="admin-login-button"
          type="submit"
          disabled={loading}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-vin text-white font-semibold px-6 py-3 rounded-full hover:bg-vin-hover transition-colors duration-200 disabled:opacity-60"
        >
          <Lock className="w-4 h-4" strokeWidth={1.75} />
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function fmtDuration(sec) {
  if (!sec) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s}s`;
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [stats, setStats] = useState(null);
  const [contribs, setContribs] = useState(null);
  const [lessons, setLessons] = useState(null);
  const [error, setError] = useState("");

  const load = async (t) => {
    try {
      const [data, c, l] = await Promise.all([adminStats(t), adminContributions(t), adminLessons(t)]);
      setStats(data);
      setContribs(c);
      setLessons(l);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        setError("Could not load statistics.");
      }
    }
  };

  const modLesson = async (id, action) => {
    await moderateLesson(token, id, action);
    const l = await adminLessons(token);
    setLessons(l);
  };
  const modComment = async (id, action) => {
    await moderateComment(token, id, action);
    const l = await adminLessons(token);
    setLessons(l);
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setStats(null);
    setContribs(null);
    setLessons(null);
  };

  const download = async (url, filename) => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const objUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(objUrl);
  };

  const downloadCsv = () => download(adminExportUrl(), "root-causes-data.csv");
  const downloadContribs = () => download(adminContributionsExportUrl(), "community-contributions.csv");

  if (!token) return <Login onSuccess={setToken} />;

  return (
    <div className="min-h-screen bg-[var(--page)]">
      <header className="sticky top-0 z-20 bg-white border-b border-[var(--line)]">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={RC_ICON} alt="" className="h-9 w-9 rounded-md" aria-hidden="true" />
            <div className="font-display font-semibold text-vin">Administrator Dashboard</div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/poster"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-poster-link"
              className="hidden sm:inline-flex items-center gap-2 text-vin text-sm font-semibold px-3 py-2 rounded-full hover:bg-vin/5 transition-colors duration-200"
            >
              QR Poster
            </a>
            <a
              href="/facilitator"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-facilitator-link"
              className="hidden sm:inline-flex items-center gap-2 text-vin text-sm font-semibold px-3 py-2 rounded-full hover:bg-vin/5 transition-colors duration-200"
            >
              Facilitator Guide
            </a>
            <a
              href="/lessons"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-lessons-link"
              className="hidden sm:inline-flex items-center gap-2 text-vin text-sm font-semibold px-3 py-2 rounded-full hover:bg-vin/5 transition-colors duration-200"
            >
              Lessons Wall
            </a>
            <button
              data-testid="export-csv-button"
              onClick={downloadCsv}
              className="inline-flex items-center gap-2 bg-vin text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-vin-hover transition-colors duration-200"
            >
              <Download className="w-4 h-4" strokeWidth={1.75} />
              Export CSV
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-ink-soft text-sm font-semibold px-3 py-2 rounded-full hover:bg-slate-100 transition-colors duration-200"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-6 py-8">
        {error && <p className="text-[#8E2929] text-sm mb-4">{error}</p>}
        {!stats ? (
          <p className="text-ink-soft">Loading…</p>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Eye} label="Total visits" value={stats.total_visits ?? 0} sub="Times the app was opened" />
              <StatCard icon={Users} label="Participants" value={stats.total_participants} sub="Started the experience" />
              <StatCard icon={CheckCircle2} label="Completed" value={stats.completed} sub={`${stats.completion_rate}% completion`} />
              <StatCard icon={Clock} label="Avg. time" value={fmtDuration(stats.avg_completion_seconds)} />
              <StatCard icon={ClipboardList} label="Community submissions" value={contribs ? contribs.total : 0} />
              <StatCard icon={Zap} label="Quick → Full year" value={stats.quick_continued ?? 0} sub={stats.quick_count ? `of ${stats.quick_count} quick snapshots` : "continued to the full year"} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <BarBlock title="Experience mode (Quick vs Full)" data={stats.mode_breakdown || []} />
              <BarBlock title="Year outcomes" data={stats.ending_profiles || []} />
              <BarBlock title="Regions represented" data={stats.region_breakdown || []} />
              <BarBlock title="Where visitors are (approx.)" data={stats.visitor_regions || []} />
              <BarBlock title="Community selections" data={stats.community_selections} />
              <BarBlock title="Most common barriers" data={stats.common_barriers} />
              <BarBlock title="What families went without" data={stats.common_sacrifices || []} />
              <BarBlock title="Neighbor profiles" data={stats.family_selections} />
              <BarBlock title="Reflections — what resonated" data={stats.reflections} />
            </div>

            <div className="bg-white rounded-lg border border-[var(--line)] p-6">
              <h3 className="font-display text-lg font-semibold text-ink mb-4">Most common decisions</h3>
              {stats.common_paths.length === 0 ? (
                <p className="text-sm text-ink-soft">No data yet.</p>
              ) : (
                <ul className="divide-y divide-[var(--line)]">
                  {stats.common_paths.map((p, i) => (
                    <li key={i} className="flex items-center justify-between py-2.5 text-sm">
                      <span className="text-ink pr-4">{p.label}</span>
                      <span className="font-semibold text-vin tabular-nums shrink-0">{p.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white rounded-lg border border-[var(--line)] p-6">
              <h3 className="font-display text-lg font-semibold text-ink mb-4">Anonymous comments</h3>
              {stats.comments.length === 0 ? (
                <p className="text-sm text-ink-soft">No comments yet.</p>
              ) : (
                <ul className="space-y-4">
                  {stats.comments.map((c, i) => (
                    <li key={i} className="border-l-2 border-vin/30 pl-4">
                      <p className="text-sm text-ink leading-relaxed">"{c.text}"</p>
                      <p className="text-xs text-ink-soft mt-1">{c.community}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white rounded-lg border border-[var(--line)] p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-semibold text-ink flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-vin" strokeWidth={1.75} />
                  Community contributions
                </h3>
                <button
                  data-testid="export-contributions-button"
                  onClick={downloadContribs}
                  className="inline-flex items-center gap-2 text-vin text-sm font-semibold px-4 py-2 rounded-full border border-vin hover:bg-vin hover:text-white transition-colors duration-200"
                >
                  <Download className="w-4 h-4" strokeWidth={1.75} />
                  Export contributions
                </button>
              </div>
              {!contribs || contribs.items.length === 0 ? (
                <p className="text-sm text-ink-soft">No community submissions yet.</p>
              ) : (
                <ul className="space-y-4">
                  {contribs.items.map((c) => (
                    <li key={c.id} data-testid="contribution-item" className="border border-[var(--line)] rounded-lg p-5">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-display font-semibold text-ink">{c.community_name}</span>
                        <span className="text-xs text-ink-soft">
                          {c.name} · {c.conference} · {c.district_council} · {c.region}
                        </span>
                      </div>
                      <p className="text-sm text-ink-soft mt-1 italic">{c.description}</p>
                      <p className="text-sm text-ink mt-3">
                        <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-vin-dark/70">Root-cause barriers: </span>
                        {c.biggest_barriers}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-soft">
                        <a href={`mailto:${c.email}`} className="text-vin hover:underline">{c.email}</a>
                        {c.data_source && <span>Source: {c.data_source}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Lessons Learned moderation */}
            <div className="bg-white rounded-lg border border-[var(--line)] p-6">
              <h3 className="font-display text-lg font-semibold text-ink mb-1 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-vin" strokeWidth={1.75} />
                Lessons Learned — moderation
              </h3>
              <p className="text-sm text-ink-soft mb-4">
                {lessons && lessons.pending_count > 0
                  ? `${lessons.pending_count} item${lessons.pending_count === 1 ? "" : "s"} awaiting approval.`
                  : "Approved posts appear on the public wall at /lessons."}
              </p>
              {!lessons || lessons.lessons.length === 0 ? (
                <p className="text-sm text-ink-soft">No lessons submitted yet.</p>
              ) : (
                <ul className="space-y-4">
                  {lessons.lessons.map((l) => (
                    <li
                      key={l.id}
                      data-testid="admin-lesson-item"
                      className={`rounded-lg border p-5 ${l.approved ? "border-[var(--line)]" : "border-amber-300 bg-amber-50/50"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <span className="font-semibold text-ink">{l.name}</span>
                          {l.community_name && <span className="text-xs text-ink-soft ml-2">· {l.community_name}</span>}
                          {!l.approved && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide font-bold text-amber-700 bg-amber-200/70 px-2 py-0.5 rounded-full">
                              Pending
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {!l.approved && (
                            <button
                              data-testid="approve-lesson-button"
                              onClick={() => modLesson(l.id, "approve")}
                              className="inline-flex items-center gap-1 text-[#365C4C] text-sm font-semibold px-3 py-1.5 rounded-full border border-[#365C4C]/40 hover:bg-[#365C4C]/10 transition-colors"
                            >
                              <Check className="w-4 h-4" strokeWidth={2} /> Approve
                            </button>
                          )}
                          <button
                            data-testid="delete-lesson-button"
                            onClick={() => modLesson(l.id, "delete")}
                            className="inline-flex items-center gap-1 text-[#8E2929] text-sm font-semibold px-3 py-1.5 rounded-full border border-[#8E2929]/30 hover:bg-[#8E2929]/10 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-ink mt-2 leading-relaxed whitespace-pre-wrap">{l.text}</p>
                      {l.comments.length > 0 && (
                        <ul className="mt-4 space-y-3">
                          {l.comments.map((c) => (
                            <li key={c.id} className={`pl-4 border-l-2 ${c.approved ? "border-[var(--line)]" : "border-amber-300"}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <span className="text-sm font-semibold text-ink">{c.name}</span>
                                  {!c.approved && (
                                    <span className="ml-2 text-[10px] uppercase tracking-wide font-bold text-amber-700 bg-amber-200/70 px-2 py-0.5 rounded-full">
                                      Pending
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {!c.approved && (
                                    <button
                                      data-testid="approve-comment-button"
                                      onClick={() => modComment(c.id, "approve")}
                                      className="inline-flex items-center gap-1 text-[#365C4C] text-xs font-semibold px-2.5 py-1 rounded-full border border-[#365C4C]/40 hover:bg-[#365C4C]/10 transition-colors"
                                    >
                                      <Check className="w-3.5 h-3.5" strokeWidth={2} /> Approve
                                    </button>
                                  )}
                                  <button
                                    data-testid="delete-comment-button"
                                    onClick={() => modComment(c.id, "delete")}
                                    className="inline-flex items-center gap-1 text-[#8E2929] text-xs font-semibold px-2.5 py-1 rounded-full border border-[#8E2929]/30 hover:bg-[#8E2929]/10 transition-colors"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                                  </button>
                                </div>
                              </div>
                              <p className="text-sm text-ink-soft mt-1 leading-relaxed whitespace-pre-wrap">{c.text}</p>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
